import React from 'react';
import { useTheme } from '@mui/material';

class StockChartBase extends React.Component {
  constructor(props) {
    super(props);
    this.chartContainerRef = React.createRef();
    this.chartInstance = null;
    this.resizeObserver = null;
    this.state = {
      chartLoaded: false,
      closeValue: null,
      flowValue: null,
      showLegend: false,
      closeCrosshairY: null,
      flowCrosshairY: null,
      currentDate: null,
    };
  }

  componentDidMount() {
    this.initChart();
    this.setupResizeObserver();
  }

  // ✅ Fix 1: componentDidUpdate condition fixed
  componentDidUpdate(prevProps) {
    if (
      prevProps.data !== this.props.data ||
      prevProps.isFullscreen !== this.props.isFullscreen ||
      prevProps.theme.palette.mode !== this.props.theme.palette.mode
    ) {
      this.initChart();
    }
  }

  componentWillUnmount() {
    if (this.chartInstance) {
      this.chartInstance.remove();
      this.chartInstance = null;
    }
    if (this.resizeObserver) {
      this.resizeObserver.disconnect();
    }
  }

  setupResizeObserver = () => {
    if (this.chartContainerRef.current && 'ResizeObserver' in window) {
      this.resizeObserver = new ResizeObserver((entries) => {
        if (!this.chartInstance || !this.chartContainerRef.current) return;
        
        requestAnimationFrame(() => {
          if (this.chartContainerRef.current) {
            const width = this.chartContainerRef.current.clientWidth;
            const height = this.chartContainerRef.current.clientHeight;
            
            if (width > 0 && height > 0) {
              this.chartInstance.applyOptions({ width, height });
              this.chartInstance.timeScale().fitContent();
            }
          }
        });
      });
      
      this.resizeObserver.observe(this.chartContainerRef.current);
    }
  };

  parseDateTime = (datetimeStr) => {
    if (!datetimeStr) return null;
    try {
      const date = new Date(datetimeStr);
      if (isNaN(date.getTime())) return null;
      return Math.floor(date.getTime() / 1000);
    } catch (error) {
      console.error('Error parsing datetime:', datetimeStr, error);
      return null;
    }
  };

  // ✅ Fix 2: dedupe helper to remove duplicate timestamps
  dedupe = (arr) => {
    const map = new Map();
    arr.forEach(item => map.set(item.time, item));
    return Array.from(map.values()).sort((a, b) => a.time - b.time);
  };

  initChart = async () => {
    try {
      const { data, theme } = this.props; 

      if (this.chartContainerRef.current) {
        this.chartContainerRef.current.innerHTML = '';
      }

      if (!this.chartContainerRef.current || !data || data.length === 0) {
        return;
      }

      const containerWidth = this.chartContainerRef.current.clientWidth;
      const containerHeight = this.chartContainerRef.current.clientHeight;

      if (containerWidth === 0 || containerHeight === 0) {
        setTimeout(this.initChart, 100);
        return;
      }

      if (!window.LightweightCharts) {
        const script = document.createElement('script');
        script.src = 'https://unpkg.com/lightweight-charts@4.1.0/dist/lightweight-charts.standalone.production.js';
        script.async = true;
        script.onload = () => this.initChart();
        document.head.appendChild(script);
        return;
      }

      const bgColor = theme?.palette?.background?.default || 'transparent';
      const textColor = theme?.palette?.text?.primary || '#9CA3AF';
      const dividerColor = theme?.palette?.divider || '#374151';

      const chart = window.LightweightCharts.createChart(this.chartContainerRef.current, {
        width: containerWidth,
        height: containerHeight,
        layout: {
          attributionLogo: false,
          background: { type: 'solid', color: theme.palette.background.default },
          textColor: theme.palette.text.primary,
        },
        timeScale: { 
          timeVisible: true, 
          rightOffset: 10,
          borderColor: dividerColor,
          barSpacing: 6,
          minBarSpacing: 0.5,
          fixLeftEdge: false,
          fixRightEdge: false,
          visible: true,
          lockVisibleTimeRangeOnResize: false,
        },
        crosshair: {
          mode: window.LightweightCharts.CrosshairMode.Normal, 
          horizontalLine: { visible: false }, 
          verticalLine: { 
            visible: true,
            color: '#7f7f7f',
            width: 2,
            style: window.LightweightCharts.LineStyle.Dashed, 
            labelVisible: true,
            labelBackgroundColor: '#2B2B43',
          },
        },
        localization: {
          priceFormatter: (price) => (price).toLocaleString('en-US', { maximumFractionDigits: 2 }),
        },
        priceScale: { mode: 0 },
        grid: {
          horzLines: { color: dividerColor },
          vertLines: { color: dividerColor },
        },
        leftPriceScale: { 
          visible: true, 
          borderColor: dividerColor,
          textColor: textColor,
          scaleMargins: { top: 0.1, bottom: 0.1 },
          mode: window.LightweightCharts.PriceScaleMode.Normal,
        },
        rightPriceScale: { 
          visible: true, 
          borderColor: dividerColor,
          textColor: textColor,
          scaleMargins: { top: 0.1, bottom: 0.1 },
          mode: window.LightweightCharts.PriceScaleMode.Normal,
        },
      });

      this.chartInstance = chart;

      // --- Processing Data ---
      // ✅ Fix 3: closeData ใช้ item.close (ไม่ใช่ net_val_accum)
      const closeData = data
        .map((item) => {
          const time = this.parseDateTime(item.datetime);
          const value = item?.close != null ? parseFloat(item.close) : 0;
          if (!time || isNaN(value)) return null;
          return { time, value };
        })
        .filter((item) => item !== null)
        .sort((a, b) => a.time - b.time);

      const flowData = data
        .map((item) => {
          const time = this.parseDateTime(item.datetime);
          const value = parseFloat(item.net_val_accum);
          if (!time || isNaN(value)) return null;
          return { 
            time, 
            value: value / 1000000,
          };
        })
        .filter((item) => item !== null)
        .sort((a, b) => a.time - b.time);

      if (closeData.length === 0 || flowData.length === 0) {
        console.warn('⚠️ No valid data after filtering');
        this.setState({ chartLoaded: false });
        return;
      }

      // --- Sampling Logic ---
      let sampledCloseData = closeData;
      let sampledFlowData = flowData;
      const dataLength = closeData.length;
      const maxDataPoints = 5000; 
      
      if (dataLength > maxDataPoints) {
        const sampleRate = Math.ceil(dataLength / maxDataPoints);
        console.log(`Downsampling from ${dataLength} to ~${Math.floor(dataLength / sampleRate)} points`);
        
        sampledCloseData = closeData.filter((_, index) => index % sampleRate === 0);
        sampledFlowData = flowData.filter((_, index) => index % sampleRate === 0);
        
        if (sampledCloseData[sampledCloseData.length - 1] !== closeData[closeData.length - 1]) {
          sampledCloseData.push(closeData[closeData.length - 1]);
        }
        if (sampledFlowData[sampledFlowData.length - 1] !== flowData[flowData.length - 1]) {
          sampledFlowData.push(flowData[flowData.length - 1]);
        }
      }

      // ✅ Fix 4: dedupe เพื่อลบ timestamp ซ้ำก่อน setData
      sampledCloseData = this.dedupe(sampledCloseData);
      sampledFlowData  = this.dedupe(sampledFlowData);

      // --- Create Series ---
      const closeSeries = chart.addLineSeries({
        color: '#ff6b35',
        lineWidth: window.innerWidth < 640 ? 2 : 3,
        priceScaleId: 'left',
        crosshairMarkerVisible: true,
        lastValueVisible: false,
        priceLineVisible: false,
      });

      const flowSeries = chart.addLineSeries({
        color: '#00d9a5',
        lineWidth: window.innerWidth < 640 ? 2 : 3,
        priceScaleId: 'right',
        crosshairMarkerVisible: true,
        lastValueVisible: false,
        priceLineVisible: false,
      });

      closeSeries.setData(sampledCloseData);
      flowSeries.setData(sampledFlowData);

      // Set Initial State
      if (sampledCloseData.length > 0) {
        this.setState({
          closeValue: sampledCloseData[sampledCloseData.length - 1].value,
          flowValue: sampledFlowData[sampledFlowData.length - 1].value,
        });
      }

      chart.subscribeCrosshairMove((param) => {
        if (!param || !param.time || param.point.x < 0 || param.point.y < 0) {
          this.setState({
            showLegend: false,
            closeCrosshairY: null,
            flowCrosshairY: null,
            currentDate: null,
          });
          return;
        }

        const closePrice = param.seriesData.get(closeSeries);
        const flowPrice = param.seriesData.get(flowSeries);

        let closeCrosshairY = null;
        let flowCrosshairY = null;

        if (closePrice) closeCrosshairY = closeSeries.priceToCoordinate(closePrice.value);
        if (flowPrice) flowCrosshairY = flowSeries.priceToCoordinate(flowPrice.value);

        let dateString = null;
        if (param.time) {
          const dateObj = new Date(param.time * 1000);
          if (!isNaN(dateObj.getTime())) {
            dateString = dateObj.toLocaleDateString('en-US', {
              month: 'short', day: 'numeric', year: 'numeric', hour: '2-digit', minute: '2-digit'
            });
          }
        }

        this.setState({
          closeValue: closePrice ? closePrice.value : null,
          flowValue: flowPrice ? flowPrice.value : null,
          mouseX: param.point?.x || 0,
          mouseY: param.point?.y || 0,
          showLegend: true,
          closeCrosshairY,
          flowCrosshairY,
          currentDate: dateString,
        });
      });

      chart.timeScale().fitContent();
      this.setState({ chartLoaded: true });

    } catch (error) {
      console.error('Error initializing chart:', error);
      this.setState({ chartLoaded: false });
    }
  };

  render() {
    const { data, isFullscreen } = this.props;
    const { chartLoaded, closeValue, flowValue, showLegend, closeCrosshairY, flowCrosshairY, currentDate } = this.state;

    const heightClass = isFullscreen
      ? 'h-full'
      : 'h-80 sm:h-96 md:h-[500px] lg:h-[600px] xl:h-[700px]';

    if (!data || data.length === 0) {
      return (
        <div className={`w-full ${heightClass}`}>
          <div className="h-full flex items-center justify-center">
            <div className="text-gray-400 text-2xl">ไม่มีข้อมูล</div>
          </div>
        </div>
      );
    }

    const shouldShowCloseLine = chartLoaded && showLegend && closeCrosshairY !== null;
    const shouldShowFlowLine = chartLoaded && showLegend && flowCrosshairY !== null;
    const shouldShowDate = chartLoaded && showLegend && currentDate;

    return (
      <div className={`w-full ${heightClass} relative`} style={{ zIndex: 0 }}>
        <div className="relative h-full px-4 sm:px-6 md:px-8" style={{ zIndex: 1 }}>

          {/* Chart Container */}
          <div ref={this.chartContainerRef} className="w-full h-full" style={{ position: 'relative', zIndex: 1 }} />

          {/* Close Line */}
          {shouldShowCloseLine && (
            <div
              key={`close-line-${closeCrosshairY}`}
              className="absolute pointer-events-none"
              style={{
                top: `${closeCrosshairY}px`,
                left: 0,
                right: 0,
                height: '1px',
                backgroundColor: '#ff6b35',
                opacity: 0.5,
                boxShadow: '0 0 12px rgba(255, 107, 53, 0.8)',
                zIndex: 1,
              }}
            />
          )}

          {/* Flow Line */}
          {shouldShowFlowLine && (
            <div
              key={`flow-line-${flowCrosshairY}`}
              className="absolute pointer-events-none"
              style={{
                top: `${flowCrosshairY}px`,
                left: 0,
                right: 0,
                height: '1px',
                backgroundColor: '#00d9a5',
                opacity: 0.5,
                boxShadow: '0 0 12px rgba(0, 217, 165, 0.8)',
                zIndex: 1,
              }}
            />
          )}

          {/* Close Value Label */}
          {shouldShowCloseLine && closeValue !== null && (
            <div
              className="absolute pointer-events-none"
              style={{
                left: '80px',
                top: `${closeCrosshairY - 16}px`,
                zIndex: 20,
                fontSize: '18px',
              }}
            >
              <div style={{
                display: 'flex',
                alignItems: 'center',
                gap: '8px',
                backgroundColor: 'rgba(17, 24, 39, 0.98)',
                padding: '6px 10px',
                borderRadius: '6px',
                backdropFilter: 'blur(8px)',
              }}>
                <span style={{ color: '#ff6b35', fontWeight: '600' }}>Close</span>
                <span style={{ color: '#ffffff', fontWeight: '600' }}>
                  {closeValue.toFixed(2)}
                </span>
              </div>
            </div>
          )}

          {/* Flow Value Label */}
          {shouldShowFlowLine && flowValue !== null && (
            <div
              className="absolute pointer-events-none"
              style={{
                right: '80px',
                top: `${flowCrosshairY - 16}px`,
                zIndex: 20,
                fontSize: '18px',
              }}
            >
              <div style={{
                display: 'flex',
                alignItems: 'center',
                gap: '8px',
                backgroundColor: 'rgba(17, 24, 39, 0.98)',
                padding: '6px 10px',
                borderRadius: '6px',
                backdropFilter: 'blur(8px)',
              }}>
                <span style={{ color: '#00d9a5', fontWeight: '600' }}>Flow</span>
                <span style={{ color: '#ffffff', fontWeight: '600' }}>
                  {flowValue.toFixed(2)}
                </span>
              </div>
            </div>
          )}

          {/* Date Display */}
          {shouldShowDate && (
            <div
              className="absolute pointer-events-none"
              style={{
                bottom: '45px',
                left: '50%',
                transform: 'translateX(-50%)',
                zIndex: 2,
              }}
            >
              <div className="bg-gray-900/95 px-3 py-1.5 rounded shadow-lg border border-gray-700">
                <span className="text-white text-sm font-semibold">
                  {currentDate}
                </span>
              </div>
            </div>
          )}

          {/* Axis Labels */}
          <div className="absolute" style={{
            fontSize: '15px',
            color: '#ff6b35',
            fontWeight: '500',
            transform: 'rotate(-90deg)',
            transformOrigin: 'left center',
            left: '15px',
            top: '50%',
            whiteSpace: 'nowrap',
            zIndex: 10,
          }}>
            Close
          </div>
          <div className="absolute" style={{
            fontSize: '15px',
            color: '#00d9a5',
            fontWeight: '500',
            transform: 'rotate(90deg)',
            transformOrigin: 'right center',
            right: '15px',
            top: '50%',
            whiteSpace: 'nowrap',
            zIndex: 10,
          }}>
            Net Flow
          </div>

          {!chartLoaded && (
            <div className="absolute inset-0 flex items-center justify-center" style={{ zIndex: 200 }}>
              <div className="text-gray-400 text-sm">กำลังโหลดกราฟ...</div>
            </div>
          )}
        </div>
      </div>
    );
  }
}

const StockChartWrapper = (props) => {
  const theme = useTheme(); 
  
  return <StockChartBase {...props} theme={theme} />;
};

export default StockChartWrapper;